import { t } from 'i18next';
import { FC, SVGProps } from 'react';

interface Props extends SVGProps<SVGSVGElement> {
  /** Duration time of the animation cycle. In `ms` or as a string @default "1s"  */
  animationDuration?: number | string;

  /** Thickness of the spinner circle stroke @default 'Light' */
  thickness?: SpinnerThickness;
}

export enum SpinnerThickness {
  Light = 2.5,
  Normal = 4,
  Bold = 6,
}

/** A spinner component to show the progression of an extended operation with unknown duration, such as a network request.
 * @param props {Object} The props object.
 * @param [props.animationDuration] {string} Duration of the spinning animation (CSS time).
 * @param [props.thickness] {string} The stroke width of the spinner (CSS length).
 * @param [props.width] {string} Width of the spinner (CSS length). Note that `SVG.width` doesn't support `rem`.
 * @param [props.height] {string} Height of the spinner (CSS length). Note that `SVG.height` doesn't support `rem`.
 * @param [props.rest] {...any} All other props are forwarded to the SVG element.
 */
export const Spinner: FC<Props> = ({
  animationDuration = '1s',
  thickness = SpinnerThickness.Light,
  width = '1em',
  height = '1em',
  ...rest
}) => {
  return (
    <svg
      aria-busy="true"
      aria-label={t('app.loading')}
      aria-live="polite"
      data-testid="spinner-image"
      height={height}
      role="status"
      viewBox="-2 -2 42 42"
      width={width}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <g fill="none" fillRule="evenodd">
        <g strokeWidth={thickness} transform="translate(1 1)">
          <circle
            cx="18"
            cy="18"
            r="18"
            stroke="currentColor"
            strokeOpacity=".2"
          />
          <path
            d="M36 18c0-9.94-8.06-18-18-18"
            stroke="currentColor"
            strokeLinecap="round"
          >
            <animateTransform
              attributeName="transform"
              dur={animationDuration}
              from="0 18 18"
              repeatCount="indefinite"
              to="360 18 18"
              type="rotate"
            />
          </path>
        </g>
      </g>
    </svg>
  );
};
